import { HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { of } from 'rxjs';
import { delay } from 'rxjs/operators';

// ---------------------------------------------------------------------------
// In-memory store (survives the session, resets on hard refresh)
// ---------------------------------------------------------------------------
let _categories: any[] = [
  {
    categoryId: 'CAT-001',
    categoryName: 'Technical Skills',
    isActive: true,
    isDeleted: false,
    createdBy: 'admin@mail.com',
    createdAt: '2026-01-01T00:00:00Z'
  },
  {
    categoryId: 'CAT-002',
    categoryName: 'Soft Skills',
    isActive: true,
    isDeleted: false,
    createdBy: 'admin@mail.com',
    createdAt: '2026-01-02T00:00:00Z'
  }
];

let _criterias: any[] = [
  {
    criteriaId: 'CR-001',
    categoryId: 'CAT-001',
    criteriaName: 'Clean Code',
    description: 'Write clean, readable, and maintainable code.',
    status: 'ACTIVE',
    isDeleted: false,
    createdAt: '2026-01-01T00:00:00Z'
  },
  {
    criteriaId: 'CR-002',
    categoryId: 'CAT-001',
    criteriaName: 'Problem Solving',
    description: 'Analyse and solve complex technical issues efficiently.',
    status: 'ACTIVE',
    isDeleted: false,
    createdAt: '2026-01-02T00:00:00Z'
  },
  {
    criteriaId: 'CR-003',
    categoryId: 'CAT-002',
    criteriaName: 'Communication',
    description: 'Communicate clearly with team members and stakeholders.',
    status: 'DRAFT',
    isDeleted: false,
    createdAt: '2026-01-03T00:00:00Z'
  }
];

let _nextCategorySeq = 100;
let _nextCriteriaSeq = 100;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function respond(status: number, body: unknown) {
  return of(new HttpResponse({ status, body })).pipe(delay(350));
}

function ok(body: unknown)      { return respond(200, body); }
function created(body: unknown) { return respond(201, body); }

function wrap(message: string, data: unknown) {
  return { status: true, message, data };
}

// ---------------------------------------------------------------------------
// URL matchers  (order matters — check `criteria_category` before `criteria`)
// ---------------------------------------------------------------------------
const RX = {
  categoryCollection: /\/criteria_category$/,
  categoryItem:       /\/criteria_category\/([^/?]+)$/,
  criteriasAll:       /\/criterias$/,
  criteriasByCategory:/\/criterias\/([^/?]+)$/,
  criteriaCollection: /\/criteria$/,
  criteriaItem:       /\/criteria\/([^/?]+)$/,
};

// ---------------------------------------------------------------------------
// Interceptor
// ---------------------------------------------------------------------------
export const mockCriteriaApiInterceptor: HttpInterceptorFn = (req, next) => {
  const url    = req.url;
  const method = req.method;

  // ── CATEGORY COLLECTION ──────────────────────────────────────────────────

  if (RX.categoryCollection.test(url) && method === 'GET') {
    const data = _categories.filter(c => !c.isDeleted);
    return ok(wrap('Successfully retrieved Criterias Category.', data));
  }

  if (RX.categoryCollection.test(url) && method === 'POST') {
    const body = req.body as any;
    const cat = {
      categoryId:   `CAT-${++_nextCategorySeq}`,
      categoryName: body.categoryName,
      isActive:     body.isActive ?? true,
      isDeleted:    false,
      createdBy:    'admin@mail.com',
      createdAt:    new Date().toISOString()
    };
    _categories.push(cat);
    console.log('[MockAPI] Category created:', cat);
    return created(wrap('Successfully Criteria Category Created.', cat));
  }

  // ── CATEGORY ITEM ────────────────────────────────────────────────────────

  const catItemMatch = RX.categoryItem.exec(url);
  if (catItemMatch) {
    const id  = catItemMatch[1];
    const idx = _categories.findIndex(c => String(c.categoryId) === String(id));

    if (method === 'PUT') {
      const body = req.body as any;
      if (idx >= 0) {
        _categories[idx] = {
          ..._categories[idx],
          categoryName: body.categoryName,
          isActive:     body.isActive ?? _categories[idx].isActive,
          updatedAt:    new Date().toISOString()
        };
        console.log('[MockAPI] Category updated:', _categories[idx]);
        return ok(wrap('Successfully Criteria Category Updated.', _categories[idx]));
      }
    }

    if (method === 'PATCH') {
      const body = req.body as any;
      if (idx >= 0) {
        if (body.isDeleted) {
          _categories[idx] = { ..._categories[idx], isDeleted: true, updatedAt: new Date().toISOString() };
          console.log('[MockAPI] Category soft-deleted:', id);
        } else if (body.isActive !== undefined) {
          _categories[idx] = { ..._categories[idx], isActive: body.isActive, updatedAt: new Date().toISOString() };
          console.log('[MockAPI] Category status updated:', id, body.isActive);
        }
        return ok(wrap('Category updated successfully.', _categories[idx]));
      }
    }
  }

  // ── ALL CRITERIAS ────────────────────────────────────────────────────────

  if (RX.criteriasAll.test(url) && method === 'GET') {
    const data = _criterias.filter(c => !c.isDeleted);
    return ok(wrap('Criterias retrieved successfully.', data));
  }

  // ── CRITERIAS BY CATEGORY ────────────────────────────────────────────────

  const criteriasByCatMatch = RX.criteriasByCategory.exec(url);
  if (criteriasByCatMatch && method === 'GET') {
    const categoryId = criteriasByCatMatch[1];
    const data = _criterias.filter(
      c => !c.isDeleted && String(c.categoryId) === String(categoryId)
    );
    return ok(wrap('Each Category Criterias retrieved successfully.', data));
  }

  // ── CRITERIA COLLECTION ──────────────────────────────────────────────────

  if (RX.criteriaCollection.test(url) && method === 'POST') {
    const body = req.body as any;
    const crit = {
      criteriaId:   `CR-${++_nextCriteriaSeq}`,
      categoryId:   body.categoryId,
      criteriaName: body.criteriaName,
      description:  body.description ?? '',
      status:       body.status ?? 'DRAFT',
      isDeleted:    false,
      createdBy:    'admin@mail.com',
      createdAt:    new Date().toISOString()
    };
    _criterias.push(crit);
    console.log('[MockAPI] Criteria created:', crit);
    return created(wrap('Criteria created successfully.', crit));
  }

  // ── CRITERIA ITEM ────────────────────────────────────────────────────────

  const critItemMatch = RX.criteriaItem.exec(url);
  if (critItemMatch) {
    const id  = critItemMatch[1];
    const idx = _criterias.findIndex(c => String(c.criteriaId) === String(id));

    if (method === 'PUT') {
      const body = req.body as any;
      if (idx >= 0) {
        _criterias[idx] = {
          ..._criterias[idx],
          criteriaName: body.criteriaName,
          description:  body.description ?? _criterias[idx].description,
          status:       body.status ?? _criterias[idx].status,
          updatedAt:    new Date().toISOString()
        };
        console.log('[MockAPI] Criteria updated:', _criterias[idx]);
        return ok(wrap('Successfully Updated Criteria Details', _criterias[idx]));
      }
    }

    if (method === 'PATCH') {
      const body = req.body as any;
      if (idx >= 0) {
        if (body.isDeleted) {
          _criterias[idx] = { ..._criterias[idx], isDeleted: true, updatedAt: new Date().toISOString() };
          console.log('[MockAPI] Criteria soft-deleted:', id);
        } else if (body.status) {
          _criterias[idx] = { ..._criterias[idx], status: body.status, updatedAt: new Date().toISOString() };
          console.log('[MockAPI] Criteria status updated:', id, body.status);
        }
        return ok(wrap('Successfully Updated Criteria Status', _criterias[idx]));
      }
    }
  }

  // ── pass through anything not matched ────────────────────────────────────
  return next(req);
};
